import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Pencil } from 'lucide-react';
import { getDocumentTemplate, type DocumentTemplateFull } from '../../lib/documents';
import DocumentRenderer from './DocumentRenderer';

interface DocumentPreviewModalProps {
  templateId: string;
  onClose: () => void;
  onEdit?: (id: string) => void;
}

export default function DocumentPreviewModal({ templateId, onClose, onEdit }: DocumentPreviewModalProps) {
  const [doc, setDoc] = useState<DocumentTemplateFull | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const full = await getDocumentTemplate(templateId);
        if (active) setDoc(full);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [templateId]);

  return createPortal(
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-stretch justify-center sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="bg-gray-100 shadow-xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] flex flex-col rounded-none sm:rounded-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white sm:rounded-t-2xl shrink-0"
          style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">Preview</span>
            {doc?.status === 'draft' && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">Draft</span>}
          </div>
          <div className="flex items-center gap-1">
            {onEdit && doc && (
              <button onClick={() => { onEdit(doc.id); onClose(); }} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium text-gray-600 hover:bg-gray-100">
                <Pencil className="h-4 w-4" /> Edit
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Paper */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}>
          {loading ? (
            <div className="flex justify-center py-16 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : !doc ? (
            <div className="text-center py-16 text-gray-400 text-sm">Document not found.</div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-xl mx-auto p-6 sm:p-8">
              <h1 className="text-2xl font-bold text-gray-900">{doc.title}</h1>
              {doc.description && <p className="text-[14px] text-gray-500 mt-1.5">{doc.description}</p>}
              <div className="h-px bg-gray-100 my-5" />
              {doc.blocks.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-8">This document has no content yet.</div>
              ) : (
                <DocumentRenderer blocks={doc.blocks} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
